import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
	NavigationMenu,
	NavigationMenuButton,
	type NavigationMenuItem,
	Tag,
} from '@ekhodealer/ekho-common/rev';

import type { NavigationItemId } from '@config/navigation/navigationItems';

import Analytics24 from '@common/components/icons/Analytics24';
import BadgePercent24 from '@common/components/icons/BadgePercent24';
import Documentation24 from '@common/components/icons/Documentation24';
import Inbox from '@common/components/icons/Inbox';
import Inventory24 from '@common/components/icons/Inventory24';
import Leads24 from '@common/components/icons/Leads24';
import PlanDetails24 from '@common/components/icons/PlanDetails24';
import ReceiptText24 from '@common/components/icons/ReceiptText24';
import Reports24 from '@common/components/icons/Reports24';
import Settings24 from '@common/components/icons/Settings24';
import { Stopwatch24 } from '@common/components/icons/Stopwatch24';
import Usage24 from '@common/components/icons/Usage24';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useDocsAuth } from '@common/hooks/useDocsAuth';
import { useNavigationItems } from '@common/hooks/useNavigationItems';
import { paths } from '@config/paths';

import './AppSideNav.scss';

// Define AppSideNavSection enum
enum AppSideNavSection {
	LEADS = 'LEADS',
	SALES = 'SALES',
	INSIGHTS = 'INSIGHTS',
	SYSTEM = 'SYSTEM',
}

// Map navigation item IDs to their display properties and handlers
const createNavigationItemMap = (
	navigate: ReturnType<typeof useNavigate>,
	servicesProviderId: string,
	openDocs: (redirectPath?: string) => Promise<void>,
	prefetchDocsToken: () => Promise<void>,
): Record<
	NavigationItemId,
	{
		icon: React.ReactNode;
		label: string;
		section: AppSideNavSection;
		onPress: () => void;
		onHoverStart?: () => void;
	}
> => ({
	leads: {
		icon: <Leads24 />,
		label: 'CRM',
		section: AppSideNavSection.LEADS,
		onPress: () => {
			navigate(paths.leads.getHref(servicesProviderId));
		},
	},
	conversations: {
		icon: <Inbox />,
		label: 'AI BDC',
		section: AppSideNavSection.LEADS,
		onPress: () => {
			navigate(paths.conversations.getHref(servicesProviderId));
		},
	},
	inventory: {
		icon: <Inventory24 />,
		label: 'Inventory',
		section: AppSideNavSection.SALES,
		onPress: () => {
			navigate(paths.inventory.getHref(servicesProviderId));
		},
	},
	orders: {
		icon: <ReceiptText24 />,
		label: 'Orders',
		section: AppSideNavSection.SALES,
		onPress: () => {
			navigate(paths.orders.getHref(servicesProviderId));
		},
	},
	preorders: {
		icon: <Stopwatch24 />,
		label: 'Preorders',
		section: AppSideNavSection.SALES,
		onPress: () => {
			navigate(paths.preorders.getHref(servicesProviderId));
		},
	},
	discounts: {
		icon: <BadgePercent24 />,
		label: 'Discounts',
		section: AppSideNavSection.SALES,
		onPress: () => {
			navigate(paths.discounts.getHref(servicesProviderId));
		},
	},
	reports: {
		icon: <Reports24 />,
		label: 'Reports',
		section: AppSideNavSection.INSIGHTS,
		onPress: () => {
			navigate(paths.reports.getHref(servicesProviderId));
		},
	},
	usage: {
		icon: <Usage24 />,
		label: 'Usage',
		section: AppSideNavSection.INSIGHTS,
		onPress: () => {
			navigate(paths.usage.getHref(servicesProviderId));
		},
	},
	analytics: {
		icon: <Analytics24 />,
		label: 'Analytics',
		section: AppSideNavSection.INSIGHTS,
		onPress: () => {
			navigate(paths.analytics.getHref(servicesProviderId));
		},
	},
	'plan-details': {
		icon: <PlanDetails24 />,
		label: 'Plan Details',
		section: AppSideNavSection.SYSTEM,
		onPress: () => {
			navigate(paths.plan_details.getHref(servicesProviderId));
		},
	},
	settings: {
		icon: <Settings24 />,
		label: 'Settings',
		section: AppSideNavSection.SYSTEM,
		onPress: () => {
			navigate(paths.settings.getHref(servicesProviderId));
		},
	},
	documentation: {
		icon: <Documentation24 />,
		label: 'Documentation',
		section: AppSideNavSection.SYSTEM,
		onPress: () => {
			// Opens docs with JWT authentication - user sees tabs based on their role
			// Backend determines default redirect based on user type (dealer, oem, ekho)
			openDocs();
		},
		onHoverStart: () => {
			// Pre-fetch JWT on hover to reduce click-to-open latency
			prefetchDocsToken();
		},
	},
});

export const AppSideNav: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { data: currentServicesProvider } = useCurrentServicesProvider();
	const servicesProviderId = currentServicesProvider?.id || 'default';
	const { openDocs, prefetchToken } = useDocsAuth();

	const isRouteSelected = useCallback(
		(pathConfig: { path: string } | null): boolean => {
			if (!pathConfig) return false;
			// Extract the route segment from the path pattern (e.g., 'leads' from '/:servicesProviderId/leads')
			const routeMatch = pathConfig.path.match(/\/:servicesProviderId\/([^/]+)/);
			if (!routeMatch) return false;
			const route = routeMatch[1];
			const expectedPath = `/${servicesProviderId}/${route}`;
			return (
				location.pathname === expectedPath ||
				location.pathname.startsWith(`${expectedPath}/`)
			);
		},
		[servicesProviderId, location.pathname],
	);

	const { items: visibleItems } = useNavigationItems();

	// Create navigation item map with handlers
	const navigationItemMap = useMemo(
		() => createNavigationItemMap(navigate, servicesProviderId, openDocs, prefetchToken),
		[navigate, servicesProviderId, openDocs, prefetchToken],
	);

	// Build menu items grouped by section
	const menuItems = useMemo((): NavigationMenuItem[] => {
		const items: NavigationMenuItem[] = [];
		const sections: Array<AppSideNavSection> = [
			AppSideNavSection.SALES,
			AppSideNavSection.LEADS,
			AppSideNavSection.INSIGHTS,
			AppSideNavSection.SYSTEM,
		];

		// Filter sections to only include those with visible items
		const sectionsWithItems = sections
			.map((section) => {
				const sectionItems = visibleItems.filter(
					(item) => navigationItemMap[item.id]?.section === section,
				);
				return { section, items: sectionItems };
			})
			.filter(({ items: sectionItems }) => sectionItems.length > 0);

		// Build menu items for each section that has visible items
		sectionsWithItems.forEach(({ section, items: sectionItems }, sectionIndex) => {
			// Add section title
			items.push({
				type: 'title',
				title: section,
			});

			// Add items for this section
			sectionItems.forEach((item) => {
				const itemConfig = navigationItemMap[item.id];
				if (!itemConfig) return;

				const button = (
					<NavigationMenuButton
						icon={itemConfig.icon}
						isSelected={item.config.path ? isRouteSelected(item.config.path) : false}
						onPress={itemConfig.onPress}
						badge={
							item.isLocked ? (
								<Tag size='small' accent={1} indicator={false}>
									New
								</Tag>
							) : undefined
						}>
						{itemConfig.label}
					</NavigationMenuButton>
				);

				items.push({
					type: 'button',
					button: itemConfig.onHoverStart ? (
						<div onMouseEnter={itemConfig.onHoverStart}>{button}</div>
					) : (
						button
					),
				});
			});

			// Add divider after section if there's another section with items after this one
			if (sectionIndex < sectionsWithItems.length - 1) {
				items.push({
					type: 'divider',
				});
			}
		});

		return items;
	}, [visibleItems, navigationItemMap, isRouteSelected]);

	return (
		<nav className={`app-side-nav ${!isOpen ? 'app-side-nav--closed' : ''}`}>
			<div className='app-side-nav-content'>
				<NavigationMenu items={menuItems} itemsSpacing='2' ariaLabel='Main navigation' />
			</div>
		</nav>
	);
};
